/** Public Menu recipe view (Demo Mode) - "pseudo public" single-recipe page linked from the
 * allergen matrix. Treat this like a real public-facing surface even though it sits behind
 * today's auth: no pricing, no internal ids beyond routing, no supplier/cost data, and
 * ingredients are named only (no quantities) - contrast with the internal Recipe Bank detail
 * page, which shows quantities. */

import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Chip, IconButton, Stack, SvgIcon, Typography } from '@mui/material';
import PageArea from '../../../components/shared/PageArea';
import PageTitle from '../../../components/shared/PageTitle';
import { Section } from '../../../components/ui/Section';
import RecipeImageTile from '../../../components/shared/RecipeImageTile';
import AllergenChips from '../../../components/shared/AllergenChips';
import { ArrowLeft } from '../../../assets';
import { menus } from '../../../mocks/data/menusData';
import { mockRecipeV2Details } from '../../../mocks/data/recipeV2Data';

const METHOD_FINAL_STEP_ANCHOR = 'public-method-final-step';

const PublicMenuRecipe = () => {
  const { t } = useTranslation('publicMenu');
  const navigate = useNavigate();
  const { id, recipeId } = useParams<{ id: string; recipeId: string }>();
  const menu = useMemo(() => menus.find((m) => m.id === Number(id)), [id]);
  const recipe = useMemo(() => mockRecipeV2Details[Number(recipeId)], [recipeId]);

  const scrollToFinalStep = () => {
    document.getElementById(METHOD_FINAL_STEP_ANCHOR)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  if (!menu || !recipe) {
    return (
      <PageArea>
        <Typography variant="textMD">{t('notFound', 'Recipe not found')}</Typography>
      </PageArea>
    );
  }

  const displayName = recipe.customerFacingName ?? recipe.description;
  const steps = recipe.steps ?? [];
  const ingredientNames = (recipe.ingredients ?? []).map((ingredient) => ingredient.name);

  return (
    <PageArea>
      <Stack direction="row"
        alignItems="center"
        spacing={2}
        sx={{ mb: 2 }}>
        <IconButton onClick={() => navigate(`/menus/${menu.id}/public`)}
          aria-label={t('backToMatrix', 'Back to allergen matrix')}>
          <SvgIcon><ArrowLeft /></SvgIcon>
        </IconButton>
        <PageTitle subTitle={recipe.shortDescription}>{displayName}</PageTitle>
      </Stack>

      <Stack spacing={4}>
        <RecipeImageTile
          imageUrl={recipe.imageUrl}
          alt={displayName}
          height={320}
          onClick={steps.length > 0 ? scrollToFinalStep : undefined}
        />

        <Section style={{ p: 4 }}>
          <Typography variant="textLG"
            fontWeight={600}
            sx={{ mb: 2 }}>
            {t('sections.ingredients', 'Ingredients')}
          </Typography>
          {ingredientNames.length === 0 ? (
            <Typography variant="textSM"
              color="text.secondary">{t('noIngredients', 'No ingredients recorded')}</Typography>
          ) : (
            <Stack direction="row"
              flexWrap="wrap"
              useFlexGap
              spacing={1}>
              {ingredientNames.map((name) => (
                <Chip key={name}
                  label={name}
                  size="small"
                  variant="outlined" />
              ))}
            </Stack>
          )}
        </Section>

        <Section style={{ p: 4 }}>
          <Typography variant="textLG"
            fontWeight={600}
            sx={{ mb: 2 }}>
            {t('sections.allergens', 'Allergens')}
          </Typography>
          <AllergenChips
            allergens={recipe.allergenSummary}
            showName
            size="medium"
          />
        </Section>

        <Section style={{ p: 4 }}>
          <Typography variant="textLG"
            fontWeight={600}
            sx={{ mb: 2 }}>
            {t('sections.nutrition', 'Nutrition')}
          </Typography>
          {/* lazy: no authored/estimated nutrition data exists in the mock catalogue yet - show
              the clear "not yet available" state rather than fabricate precise figures. */}
          <Typography variant="textSM"
            color="text.secondary">
            {t('nutritionNotAvailable', 'Nutritional information is not yet available for this recipe.')}
          </Typography>
        </Section>

        <Section style={{ p: 4 }}>
          <Typography variant="textLG"
            fontWeight={600}
            sx={{ mb: 2 }}>
            {t('sections.method', 'Method')}
          </Typography>
          {steps.length === 0 ? (
            <Typography variant="textSM"
              color="text.secondary">{t('noSteps', 'No method steps recorded')}</Typography>
          ) : (
            <Stack spacing={2}>
              {steps.map((step, index) => {
                const isLastStep = index === steps.length - 1;
                return (
                  <Box
                    key={step.id}
                    id={isLastStep ? METHOD_FINAL_STEP_ANCHOR : undefined}
                    sx={{
                      display: 'flex',
                      gap: 2,
                      p: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: '12px',
                    }}
                  >
                    {isLastStep && recipe.imageUrl && (
                      <Box
                        component="img"
                        src={recipe.imageUrl}
                        alt={displayName}
                        sx={{ width: 96, height: 96, borderRadius: '8px', objectFit: 'cover', flexShrink: 0 }}
                      />
                    )}
                    <Box>
                      <Chip label={step.stepNumber}
                        size="small"
                        color="primary"
                        sx={{ mb: 1, fontWeight: 700 }} />
                      <Typography variant="textMD"
                        fontWeight={600}>
                        {step.title}
                      </Typography>
                      <Typography variant="textSM"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}>
                        {step.instruction}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          )}
        </Section>
      </Stack>
    </PageArea>
  );
};

export default PublicMenuRecipe;
